import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import BaseMatManModal from '@monorepo/shared/src/components/common/Modals/BaseMatManModal';
import BaseModalTitle from '@monorepo/shared/src/components/common/Modals/BaseModalTitle';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import useReports from 'state/hooks/use-reports';
import theme, {
  FONT_GSS_FUTURA_BOOK,
  FONT_GSS_FUTURA_SEMI_BOLD,
} from '@monorepo/shared/src/themes/defaultTheme';
import {
  remCalc,
  translate,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import useAssignmentStore from 'graph/state/query-hooks/use-assignments-store';

const t = (key: string, options?) => translate('reports', key, options);

type Props = {
  student: any;
  character: string | undefined;
  openSkipAssignmentModal: boolean;
  isUnskip: boolean;
  handleClose?: () => void;
  setOpenSkipAssignmentModal: (toggleModal: boolean) => void;
};

const useSkipAssignment = ({
  student,
  character,
  openSkipAssignmentModal,
  handleClose,
  isUnskip,
  setOpenSkipAssignmentModal,
}: Props) => {
  const classes = useStyles();
  const [, { openModal, closeModal }] = useModal();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [, { refetch: refetchProgress }] = useReports();
  const [{ assignments }, { handleSkipAssignment, handleUnskipAssignment, refetch }] =
    useAssignmentStore();

  const handleExit = () => {
    setOpenSkipAssignmentModal(false);
    closeModal();
  };

  const skipAssignments = async () => {
    const assignment = assignments.find((c) => c.character.character === character);
    const result = assignment && (await handleSkipAssignment(assignment.id, [student.id]));
    if (!result || result.errors) {
      setErrorMessage('There was an error skipping this assignment');
      return;
    }
    closeModal();
    handleClose && handleClose();
    refetch();
    refetchProgress();
  };

  const unskipAssignments = async () => {
    const assignment = assignments.find((c) => c.character.character === character);
    const result = assignment && (await handleUnskipAssignment(assignment.id, [student.id]));
    if (!result || result.errors) {
      setErrorMessage('There was an error unskipping this assignment');
      return;
    }
    closeModal();
    handleClose && handleClose();
    refetch();
    refetchProgress();
  };

  useEffect(() => {
    openSkipAssignmentModal &&
      openModal({
        Title: () => (
          <BaseModalTitle
            text={isUnskip ? t('unskipAssignment') : t('skipAssignment')}
            onClose={handleExit}
          />
        ),
        body: (
          <BaseMatManModal
            leftButtonAction={handleExit}
            rightButtonAction={isUnskip ? unskipAssignments : skipAssignments}
            headerText={isUnskip ? t('unskipAssignmentHeader') : t('skipAssignmentHeader')}
            headerFontSize={isUnskip ? '22px' : '24px'}
            leftButtonText={t('cancel')}
            rightButtonText={isUnskip ? t('unskipAssignment') : t('skipAssignment')}
            rightButtonIcon={faCheckCircle}
          >
            <Typography className={classes.modalText}>
              {t(isUnskip ? 'unskipAssignmentBody' : 'skipAssignmentBody', {
                name: student.name ?? `${student.firstName}  ${student.lastName}`,
                character,
              })}
            </Typography>
            {errorMessage && <Typography className={classes.error}>{errorMessage}</Typography>}
          </BaseMatManModal>
        ),
      });
  }, [openSkipAssignmentModal, isUnskip]);
};

const useStyles = makeStyles(() => ({
  modalHeader: {
    fontSize: remCalc(24),
    lineHeight: remCalc(36),
    marginBottom: remCalc(12),
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
  },
  modalText: {
    fontSize: remCalc(18),
    lineHeight: remCalc(28),
    marginBottom: remCalc(12),
    fontFamily: FONT_GSS_FUTURA_BOOK,
  },
  error: {
    fontSize: remCalc(16),
    color: theme.palette.error.main,
  },
}));

export default useSkipAssignment;
